import type { ColDef } from './poolColDefs';

export interface ApiAssignment {
  taskId: number;
  evaluatee: {
    id: number;
    name: string;
    position: string | null;
    deptPath: string | null;
    talentPool: string;
    directLeaderId: number | null;
  };
  roleInEval: string;
  status: string;
  source?: TaskSource;
  overrideReason?: string | null;
  voidReason?: string | null;
  template: { id: number; pool: string };
}

export type TaskSource = 'ORG_AUTO' | 'MANUAL' | 'MANUAL_OVERRIDE';

export interface TaskMetadata {
  id: number;
  source: TaskSource;
  overrideReason: string | null;
  voidReason: string | null;
}

export interface ApiGroup {
  evaluator: {
    id: number;
    name: string;
    position: string | null;
    deptPath: string | null;
    talentPool: string;
  };
  totalTasks: number;
  pending: number;
  drafted: number;
  submitted: number;
  assignments: ApiAssignment[];
}

export interface EvaluatorRef {
  taskId: number;
  evaluatorId: number;
  evaluatorName: string;
  position: string | null;
  deptPath: string | null;
  status: string;
  roleInEval: string;
  source?: TaskSource;
  overrideReason?: string | null;
  voidReason?: string | null;
}

export interface PivotRow {
  evaluatee: {
    id: number;
    name: string;
    position: string | null;
    deptPath: string | null;
    directLeaderId: number | null;
  };
  pool: string;
  byRole: Record<string, EvaluatorRef[]>;
  allTasks: EvaluatorRef[];
}

export interface RowAllocation {
  perCol: EvaluatorRef[][];
  unassigned: EvaluatorRef[];
}

export function buildPivotRows(
  groups: ApiGroup[],
  metadataByTaskId: ReadonlyMap<number, TaskMetadata> = new Map(),
): PivotRow[] {
  const map = new Map<number, PivotRow>();
  for (const group of groups) {
    for (const assignment of group.assignments) {
      let row = map.get(assignment.evaluatee.id);
      if (!row) {
        row = {
          evaluatee: {
            id: assignment.evaluatee.id,
            name: assignment.evaluatee.name,
            position: assignment.evaluatee.position,
            deptPath: assignment.evaluatee.deptPath,
            directLeaderId: assignment.evaluatee.directLeaderId,
          },
          pool: assignment.template?.pool || assignment.evaluatee.talentPool,
          byRole: {},
          allTasks: [],
        };
        map.set(assignment.evaluatee.id, row);
      }
      const ref: EvaluatorRef = {
        taskId: assignment.taskId,
        evaluatorId: group.evaluator.id,
        evaluatorName: group.evaluator.name,
        position: group.evaluator.position,
        deptPath: group.evaluator.deptPath,
        status: assignment.status,
        roleInEval: assignment.roleInEval,
        source: assignment.source ?? metadataByTaskId.get(assignment.taskId)?.source,
        overrideReason:
          assignment.overrideReason ?? metadataByTaskId.get(assignment.taskId)?.overrideReason,
        voidReason: assignment.voidReason ?? metadataByTaskId.get(assignment.taskId)?.voidReason,
      };
      (row.byRole[assignment.roleInEval] ||= []).push(ref);
      row.allTasks.push(ref);
    }
  }
  for (const row of map.values()) {
    Object.values(row.byRole).forEach((items) => items.sort((a, b) => a.taskId - b.taskId));
    row.allTasks.sort((a, b) => a.taskId - b.taskId);
  }
  return Array.from(map.values()).sort((a, b) =>
    a.evaluatee.name.localeCompare(b.evaluatee.name, 'zh-CN'),
  );
}

export function allocateRowSlots(row: PivotRow, cols: ColDef[]): RowAllocation {
  const tasks = [...row.allTasks].sort((a, b) => a.taskId - b.taskId);
  const perCol = cols.map(() => [] as EvaluatorRef[]);
  const unassigned: EvaluatorRef[] = [];
  const tasksByRole = new Map<string, EvaluatorRef[]>();
  tasks.forEach((task) => {
    const items = tasksByRole.get(task.roleInEval) ?? [];
    items.push(task);
    tasksByRole.set(task.roleInEval, items);
  });
  tasksByRole.forEach((roleTasks, role) => {
    const matchingIndexes = cols.flatMap((col, index) =>
      col.roles.includes(role) ? [index] : [],
    );
    if (!matchingIndexes.length) {
      unassigned.push(...roleTasks);
      return;
    }
    roleTasks.forEach((task, index) => {
      const targetIndex = matchingIndexes[Math.min(index, matchingIndexes.length - 1)];
      perCol[targetIndex].push(task);
    });
  });
  return {
    perCol,
    unassigned: unassigned.sort((a, b) => a.taskId - b.taskId),
  };
}

export function rowKeyOf(row: PivotRow): string {
  return `${row.pool}-${row.evaluatee.id}`;
}

export function pivotRowCompletionPercent(row: PivotRow): number {
  if (!row.allTasks.length) return 0;
  const submitted = row.allTasks.filter((task) => task.status === 'SUBMITTED').length;
  return Math.round((submitted / row.allTasks.length) * 100);
}

export function isAnomaly(
  row: PivotRow,
  col: ColDef,
  evaluator: EvaluatorRef | null | undefined,
  acks: Set<number>,
): boolean {
  if (!evaluator || col.anomalyCheck !== 'direct' || acks.has(evaluator.taskId)) return false;
  return (
    row.evaluatee.directLeaderId != null &&
    evaluator.evaluatorId !== row.evaluatee.directLeaderId
  );
}

export function rowHasMissing(allocation: RowAllocation | undefined): boolean {
  return !!allocation && (
    allocation.unassigned.length > 0 || allocation.perCol.some((evaluators) => !evaluators.length)
  );
}

export function rowHasAnomaly(
  row: PivotRow,
  cols: ColDef[],
  allocation: RowAllocation | undefined,
  acks: Set<number>,
): boolean {
  if (!allocation) return false;
  return cols.some((col, index) =>
    allocation.perCol[index].some((evaluator) => isAnomaly(row, col, evaluator, acks)),
  );
}
